// Dev-only demo mode: a signed-in fake user and an in-memory API, so every page
// can be clicked through without Firebase or a backend. Turned on by visiting
// /__demo in `npm run dev` (off again at /__demo/off). Never active in a build.
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { Company, CompanyStatus, HistoryChange, HistorySnapshot } from './types'

const KEY = 'metafor-demo'

export function isDemo() {
  if (!import.meta.env.DEV) return false
  try {
    return sessionStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

export function setDemo(on: boolean) {
  try {
    if (on) sessionStorage.setItem(KEY, '1')
    else sessionStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

export const demoUser = {
  uid: 'demo',
  email: 'anna.lind@example.com',
  displayName: 'Anna Lind',
  emailVerified: true,
  photoURL: null,
  providerData: [{ providerId: 'password' }],
  reload: async () => {},
  getIdToken: async () => 'demo',
  // read by useImpersonation; the demo user is never impersonated
  getIdTokenResult: async () => ({ claims: {} }),
}

// ---- fixtures ------------------------------------------------------------

function rng(seed: number) {
  return () => {
    seed = (seed * 16807) % 2147483647
    return (seed - 1) / 2147483646
  }
}
const rand = rng(42)
const pick = <T,>(a: readonly T[]) => a[Math.floor(rand() * a.length)]

const NAMES = ['Nordvik Logistik', 'Kallax Fastigheter', 'Solberga Energi', 'Hamnkontoret', 'Ekdal & Partners', 'Brunnsvik Bygg', 'Fjällräv Media', 'Ljusdal Konsult', 'Östermalm Dental', 'Sjöberg Revision', 'Tallbacka Skog', 'Vinga Marin', 'Gröna Lund Catering', 'Klara IT', 'Bergslagen Stål', 'Runö Resor', 'Lindholmen Labs', 'Hallands Hotell', 'Norrsken Data', 'Värmdö Varv', 'Aurora Analys', 'Björkhaga Skolor', 'Dalarö Design', 'Enköping Elektro', 'Frösö Finans', 'Gotland Gårdar', 'Hjo Hälsa', 'Idre Invest', 'Järna Juridik', 'Kiruna Kraft', 'Lerum Logistik', 'Mora Mekanik', 'Nacka Nät', 'Orsa Optik', 'Piteå Plast', 'Rättvik Reklam', 'Sala Säkerhet', 'Tranås Transport', 'Umeå Utbildning', 'Vallentuna VVS', 'Ystad Yoga', 'Åre Äventyr', 'Älmhult Möbler', 'Öland Ost']
const PEOPLE = ['Anna Lind', 'Erik Berg', 'Sara Nyström', 'Johan Ek', 'Maja Holm', 'Oskar Dahl']
const INDUSTRIES = ['Logistics', 'Real estate', 'Energy', 'Consulting', 'Healthcare', 'Media', 'Manufacturing', 'Hospitality', 'Software']
const MARKETS = ['Sweden', 'Norway', 'Finland', 'Denmark']
const SOURCES = ['hubspot', 'hubspot', 'importXls', 'manual', 'zapier'] as const
const STATUS_W: CompanyStatus[] = ['customer', 'customer', 'customer', 'lead', 'lead', 'opportunity', 'churned', 'lost_lead', 'lost_opportunity']
const DAY = 86_400_000

let nextId = 1
const records: Company[] = []

for (const [i, base] of NAMES.entries()) {
  const crmId = String(10200 + i * 7)
  const name = i % 3 === 0 ? `${base} AB` : base
  const owner = pick(PEOPLE)
  const industry = pick(INDUSTRIES)
  const market = pick(MARKETS)
  const source = pick(SOURCES)
  const count = 1 + Math.floor(rand() * 4)
  let status: CompanyStatus = rand() < 0.5 ? 'lead' : 'opportunity'
  let mrr = 0
  let when = Date.now() - (30 + Math.floor(rand() * 400)) * DAY
  for (let r = 0; r < count; r++) {
    if (r === count - 1) status = pick(STATUS_W)
    else if (r > 0) status = status === 'lead' ? 'opportunity' : 'customer'
    if (status === 'customer') mrr = mrr ? mrr + Math.round(rand() * 4) * 500 : 2000 + Math.round(rand() * 20) * 500
    if (status === 'churned') mrr = 0
    const at = new Date(when).toISOString()
    records.push({
      Id: nextId++, name, mrr, status, crmId, csmId: rand() < 0.5 ? `CSM-${100 + i}` : '', orgno: `55${String(6000000 + i * 1337).slice(0, 7)}-${1000 + i}`,
      DateCreated: at, owner, salesRep: pick(PEOPLE), leadSource: pick(['Website', 'Referral', 'Event', 'Outbound']), Industry: industry, Market: market,
      ActivityLevel: String(1 + Math.floor(rand() * 5)), LeadStatus: status === 'lead' ? 'open' : status === 'lost_lead' ? 'lost' : '', Source: source,
      Active: status !== 'churned', EventType: r === 0 ? (status === 'customer' ? 'New customer' : 'New lead') : status === 'customer' ? (r > 1 ? 'Upsell' : 'New customer') : status === 'churned' ? 'Churn' : status === 'lost_lead' ? 'Lost lead' : 'Data Change',
      EventTypeMrrChange: status === 'customer' ? 500 * (1 + Math.floor(rand() * 3)) : null, CreatedBy: 'demo', CreatedAt: at,
      customFields: [{ fieldId: 1, fieldName: 'Region', fieldValue: pick(['North', 'South', 'West', 'East']) }],
    })
    when += (5 + Math.floor(rand() * 60)) * DAY
    if (when > Date.now()) when = Date.now() - Math.floor(rand() * 3) * DAY - Math.floor(rand() * 20) * 3_600_000
  }
}

const latestOf = (crmId: string) =>
  records.filter((r) => r.crmId === crmId).sort((a, b) => +new Date(b.DateCreated) - +new Date(a.DateCreated))[0]
const latestAll = () => [...new Set(records.map((r) => r.crmId))].map(latestOf)

// field label, API path segment, record key — what the history endpoints return
const FIELDS: [string, string, keyof Company][] = [
  ['Name', 'name', 'name'], ['Status', 'status', 'status'], ['MRR', 'mrr', 'mrr'], ['CSM ID', 'csmId', 'csmId'], ['Industry', 'industry', 'Industry'],
  ['Lead Source', 'leadSource', 'leadSource'], ['Lead Status', 'leadStatus', 'LeadStatus'], ['Market', 'market', 'Market'],
  ['Organization Number', 'orgno', 'orgno'], ['Owner', 'owner', 'owner'], ['Sales Rep', 'salesRep', 'salesRep'], ['Activity Level', 'activityLevel', 'ActivityLevel'],
]

function history(crmId: string, full: boolean): HistorySnapshot[] {
  const rs = records.filter((r) => r.crmId === crmId).sort((a, b) => +new Date(a.DateCreated) - +new Date(b.DateCreated))
  const out = rs.map((r, i) => {
    const prev = rs[i - 1]
    const changes: HistoryChange[] = FIELDS.map(([label, db, key]) => ({
      changed_field: label,
      db_field_name: db,
      previous_value: prev ? String(prev[key] ?? '') : '',
      current_value: String(r[key] ?? ''),
    })).filter((c) => full || c.previous_value !== c.current_value)
    return { id: r.Id, changes, isFirstRow: i === 0, created: r.DateCreated, source: r.Source }
  })
  return out.reverse()
}

// ---- assistant -----------------------------------------------------------
// A scripted stand-in for the AI assistant: same SSE events and approval
// flow as the API (modules/ai_agent), with canned replies.

type ChatRow = { id: number; payload: Record<string, unknown>; call_approval: number; created_at: string }
const chatRows: ChatRow[] = []
let chatRowId = 1
const addRow = (payload: Record<string, unknown>, call_approval = 0) => {
  chatRows.push({ id: chatRowId++, payload, call_approval, created_at: new Date().toISOString() })
}

type Script = { text: string; tool?: { name: string; access: 'read' | 'write'; display: string; args: Record<string, unknown>; summary?: string } }

function scriptFor(message: string): Script {
  const m = message.toLowerCase()
  const email = message.match(/[\w.+-]+@[\w-]+\.[\w.]+/)?.[0]
  if (m.includes('invite') && email) {
    const role = m.includes('admin') ? 'admin' : 'user'
    return {
      text: `I'll invite **${email}** as ${role === 'admin' ? 'an admin' : 'a user'}. They'll get an email with a link to join.`,
      tool: { name: 'invite_user', access: 'write', display: 'Invite User', args: { email, role }, summary: `Invite user ${email} to the team with role: ${role}` },
    }
  }
  if (m.includes('rename') && m.includes('team')) {
    const name = message.match(/\bto\s+(.+?)\.?$/i)?.[1]?.replace(/^["“']|["”']$/g, '') ?? 'New name'
    return { text: `Renaming the team to **${name}**.`, tool: { name: 'rename_team', access: 'write', display: 'Rename Team', args: { name }, summary: `Rename team to: ${name}` } }
  }
  if (m.includes('create') && (m.includes('company') || m.includes('customer'))) {
    const name = message.match(/called\s+(.+?)\.?$/i)?.[1] ?? 'New company'
    const args = { name, status: 'lead', mrr: 0, crmId: `AI-${Math.floor(1000 + Math.random() * 9000)}` }
    return { text: `Here's the company I'll create. It starts as a lead with no MRR; change that after if you like.`, tool: { name: 'create_company', access: 'write', display: 'Create Company', args, summary: `Create company: ${name}` } }
  }
  if (m.includes('dashboard') || m.includes('summar') || m.includes('overview')) return { text: '', tool: { name: 'dashboard_stats', access: 'read', display: 'Dashboard Stats', args: {} } }
  if (m.includes('customer') || m.includes('mrr') || m.includes('lead') || m.includes('compan')) {
    const status = m.includes('lead') ? 'lead' : 'customer'
    return { text: '', tool: { name: 'list_companies', access: 'read', display: 'List Companies', args: { status } } }
  }
  return {
    text: "I can look things up and make changes for you. For example:\n\n- **Find companies** by status, MRR or name\n- **Summarize** the dashboard\n- **Create a company**, **invite a teammate** or **rename the team** (you approve each change first)\n\nWhat would you like to do?",
  }
}

function toolResult(name: string, args: Record<string, unknown>): Record<string, unknown> {
  const all = latestAll()
  if (name === 'list_companies') {
    const list = all.filter((c) => c.status === args.status).sort((a, b) => b.mrr - a.mrr)
    return { success: true, total: list.length, companies: list.slice(0, 5).map((c) => ({ name: c.name, crmId: c.crmId, mrr: c.mrr, status: c.status })) }
  }
  if (name === 'dashboard_stats') {
    const counts: Record<string, number> = {}
    all.forEach((c) => (counts[c.status] = (counts[c.status] ?? 0) + 1))
    return { success: true, statusCounts: counts, mrr: all.reduce((s, c) => s + (c.status === 'customer' ? c.mrr : 0), 0) }
  }
  if (name === 'invite_user') {
    members.push({ id: Math.max(...members.map((x) => x.id)) + 1, user_id: `u-${Date.now()}`, team_id: 1, role: String(args.role), email: String(args.email), name: '' })
    return { success: true, message: `User ${args.email} has been invited to the team with role: ${args.role}`, email: args.email, role: args.role }
  }
  if (name === 'rename_team') {
    team.team.name = String(args.name)
    return { success: true, message: `Team renamed to ${args.name}` }
  }
  if (name === 'create_company') {
    const at = new Date().toISOString()
    records.push({ ...records[0], Id: nextId++, name: String(args.name), status: 'lead', mrr: 0, crmId: String(args.crmId), DateCreated: at, CreatedAt: at, Source: 'manual', EventType: 'New lead', customFields: [] })
    return { success: true, message: `Company '${args.name}' created successfully`, company: { name: args.name, crmId: args.crmId, status: 'lead' } }
  }
  return { success: true }
}

function followUp(): string {
  const out = [...chatRows].reverse().find((r) => r.payload.type === 'function_call_output')
  if (!out) return 'Anything else?'
  const o = JSON.parse(String(out.payload.output)) as { status: string; result?: Record<string, unknown> }
  const r = o.result ?? {}
  if (o.status === 'denied') return "Okay, I didn't change anything."
  switch (out.payload.name) {
    case 'list_companies': {
      const list = (r.companies as { name: string; crmId: string; mrr: number }[]) ?? []
      if (!list.length) return 'There are no companies with that status yet.'
      return `There are **${r.total}** of them. The largest by MRR:\n\n| Company | MRR |\n| --- | ---: |\n${list.map((c) => `| [${c.name}](/companies/details/${c.crmId}) | ${c.mrr.toLocaleString('sv-SE')} kr |`).join('\n')}`
    }
    case 'dashboard_stats': {
      const c = r.statusCounts as Record<string, number>
      return `Right now the team tracks **${Object.values(c).reduce((a, b) => a + b, 0)}** companies: ${c.customer ?? 0} customers, ${c.opportunity ?? 0} opportunities and ${c.lead ?? 0} leads. Customers bring in **${Number(r.mrr).toLocaleString('sv-SE')} kr** MRR a month; ${c.churned ?? 0} have churned.`
    }
    case 'invite_user':
      return `Done. The invitation is on its way; they appear under [Team](/settings/team) once it's sent.`
    case 'rename_team':
      return 'Done. The new name shows in the team switcher.'
    case 'create_company':
      return 'Created. Open it to fill in the rest of the details.'
  }
  return 'Done.'
}

export function demoAssistantStream(body: { data?: { type: string; content: string } | null }, signal: AbortSignal): Response {
  const sse = (event: string, data: unknown) => `event: ${event}\ndata: ${typeof data === 'string' ? data : JSON.stringify(data)}\n\n`
  let script: Script
  if (body.data?.type === 'message') {
    addRow({ role: 'user', content: body.data.content })
    script = scriptFor(body.data.content)
  } else {
    script = { text: followUp() }
  }
  const words = script.text.match(/\S+\s*|\n+/g) ?? []
  const enc = new TextEncoder()
  const stream = new ReadableStream<Uint8Array>({
    async start(ctrl) {
      const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))
      ctrl.enqueue(enc.encode(sse('chat-metadata', { id: 1, title: null })))
      await wait(script.text ? 500 : 700)
      let said = ''
      for (const w of words) {
        if (signal.aborted) break
        said += w
        ctrl.enqueue(enc.encode(sse('message', { chunk: w })))
        await wait(28)
      }
      if (said) addRow({ role: 'assistant', content: said })
      if (script.tool && !signal.aborted) {
        const callId = `call_${Date.now().toString(36)}`
        addRow({ type: 'function_call', call_id: callId, name: script.tool.name, arguments: JSON.stringify(script.tool.args) })
        ctrl.enqueue(enc.encode(sse('tool-end', { callId, name: script.tool.name, arguments: script.tool.args, displayName: script.tool.display, accessLevel: script.tool.access, summary: script.tool.summary })))
      }
      ctrl.enqueue(enc.encode(sse('message', '[DONE]')))
      ctrl.close()
    },
  })
  return new Response(stream, { status: 200, headers: { 'Content-Type': 'text/event-stream' } })
}

function executeDemoTool(callId: string, approval: number) {
  const row = chatRows.find((r) => r.payload.call_id === callId && r.payload.type === 'function_call' && r.call_approval === 0)
  if (!row) return null
  row.call_approval = approval
  const name = String(row.payload.name)
  const save = (status: string, result: unknown) => addRow({ type: 'function_call_output', call_id: callId, name, output: JSON.stringify({ status, result, error: null }) })
  if (approval === 2) {
    save('denied', null)
    return { success: true, status: 'denied', result: null, error: null, toolName: name, callId }
  }
  const result = toolResult(name, JSON.parse(String(row.payload.arguments)))
  save('completed', result)
  return { success: true, status: 'completed', result, error: null, toolName: name, callId }
}

// ---- adapter -------------------------------------------------------------

const team = {
  team: { Id: 1, name: 'Metafor Demo', CustomersApiUrl: null, date_time_format: 'YYYY-MM-DD HH:mm', currency: 'SEK' },
  role: 'admin',
  plan: { Name: 'Premium', Features: ['crm_integrations', 'custom_fields', 'alerts'], Limits: { companies: 100, users: 5, integrations: 3 }, BillingType: 'monthly', Price: 0 },
  expiresAt: null,
}
const members = [
  { id: 1, user_id: 'demo', team_id: 1, role: 'admin', email: 'anna.lind@example.com', name: 'Anna Lind' },
  { id: 2, user_id: 'u-erik', team_id: 1, role: 'admin', email: 'erik.berg@example.com', name: 'Erik Berg' },
  { id: 3, user_id: 'u-sara', team_id: 1, role: 'user', email: 'sara.nystrom@example.com', name: 'Sara Nyström' },
  { id: 4, user_id: 'u-johan', team_id: 1, role: 'user', email: 'johan.ek@example.com', name: '' },
]
let subscription: Record<string, unknown> | null = {
  id: 1, team_id: 1, subscription_id: 'sub_demo', customer_id: 'cus_demo', internal_plan_id: 'premium_yearly',
  features: 'crm_integrations,custom_fields', expired_at: new Date(Date.now() + 200 * DAY).toISOString(), cancel_at_period_end: false,
}
let hubspot: Record<string, unknown> | null = {
  hub_domain: 'metafor-demo.hubspot.com', hub_id: 4411, user: 'anna.lind@example.com', created_at: new Date(Date.now() - 90 * DAY).toISOString(),
  sync_status: 'success', last_sync_success_date: new Date(Date.now() - 3 * 3_600_000).toISOString(),
  csm_id_mapping: '', mrr_mapping: 'annualrevenue', orgno_mapping: 'org_number', owner_mapping: '', sales_rep_mapping: 'hubspot_owner_id', activity_level_mapping: '',
  hubspot_status_field: 'lifecyclestage', historical_records_start_date: '2024-01-01', only_update_existing_companies: false,
  hubspot_statuses_to_sync: '["lead","opportunity","customer"]', status_customer_mapping: '["customer"]', status_opportunity_mapping: '["opportunity"]',
  status_lead_mapping: '["lead","marketingqualifiedlead"]', status_churned_mapping: '[]', status_lost_lead_mapping: '[]', status_lost_opportunity_mapping: '[]',
  custom_field_mappings: [{ '1': 'region' }],
}
const hubspotFields = [
  { name: 'name', label: 'Company name', type: 'string', options: [] },
  { name: 'domain', label: 'Company domain', type: 'string', options: [] },
  { name: 'org_number', label: 'Organisation number', type: 'string', options: [] },
  { name: 'region', label: 'Region', type: 'string', options: [] },
  { name: 'hubspot_owner_id', label: 'Company owner', type: 'string', options: [] },
  { name: 'annualrevenue', label: 'Annual revenue', type: 'number', options: [] },
  { name: 'mrr', label: 'MRR', type: 'number', options: [] },
  { name: 'numberofemployees', label: 'Number of employees', type: 'number', options: [] },
  { name: 'lifecyclestage', label: 'Lifecycle stage', type: 'enumeration', options: [
    { label: 'Subscriber', value: 'subscriber' }, { label: 'Lead', value: 'lead' }, { label: 'Marketing qualified lead', value: 'marketingqualifiedlead' },
    { label: 'Sales qualified lead', value: 'salesqualifiedlead' }, { label: 'Opportunity', value: 'opportunity' }, { label: 'Customer', value: 'customer' },
    { label: 'Evangelist', value: 'evangelist' }, { label: 'Other', value: 'other' },
  ] },
  { name: 'hs_lead_status', label: 'Lead status', type: 'enumeration', options: [
    { label: 'New', value: 'NEW' }, { label: 'Open', value: 'OPEN' }, { label: 'In progress', value: 'IN_PROGRESS' }, { label: 'Unqualified', value: 'UNQUALIFIED' },
  ] },
]
let zapierKey: string | null = null
const customFields = [
  { id: 1, name: 'Region', createdAt: new Date(Date.now() - 120 * DAY).toISOString() },
  { id: 2, name: 'Contract length', createdAt: new Date(Date.now() - 40 * DAY).toISOString() },
]
const fieldName = (id: number) => customFields.find((f) => f.id === id)?.name ?? ''
// mirrors api modules/alerts for a Premium team (catalog order = priority)
type DemoTrigger = { field: string; operator: string; value: number | string } | null
const tr = (field: string, operator: string, value: number | string): DemoTrigger => ({ field, operator, value })
const alertDefaults: { id: string; enabled: boolean; trigger: DemoTrigger }[] = [
  { id: 'crisis', enabled: true, trigger: tr('mrr_change_90d_pct', '<', -40) },
  { id: 'urgent', enabled: false, trigger: null },
  { id: 'attention', enabled: false, trigger: null },
  { id: 'blocked', enabled: false, trigger: null },
  { id: 'declining', enabled: true, trigger: tr('mrr_change_90d_pct', '<', -15) },
  { id: 'unhappy', enabled: false, trigger: null },
  { id: 'needs-input', enabled: false, trigger: null },
  { id: 'person-needed', enabled: false, trigger: null },
  { id: 'maintenance', enabled: false, trigger: null },
  { id: 'idle', enabled: true, trigger: tr('days_since_change', '>', 30) },
  { id: 'delivery', enabled: false, trigger: null },
  { id: 'in-progress', enabled: false, trigger: null },
  { id: 'conversation', enabled: false, trigger: null },
  { id: 'opportunity', enabled: false, trigger: tr('days_in_status', '<', 14) },
  { id: 'money-event', enabled: false, trigger: tr('mrr', '>', 10000) },
  { id: 'high-activity', enabled: false, trigger: tr('changes_30d', '>', 3) },
  { id: 'improving', enabled: false, trigger: tr('mrr_change_90d_pct', '>', 15) },
  { id: 'completed', enabled: false, trigger: tr('days_in_status', '<', 30) },
  { id: 'happy', enabled: false, trigger: null },
  { id: 'notable-event', enabled: false, trigger: tr('mrr_percentile', '>', 90) },
]
const alerts = structuredClone(alertDefaults)
const NUM = ['<', '>', '=']
const alertFields = () => [
  { key: 'mrr', label: 'MRR', type: 'number', operators: NUM },
  { key: 'employees', label: 'Employees', type: 'number', operators: NUM },
  { key: 'status', label: 'Status', type: 'enum', operators: ['='], options: ['lead', 'opportunity', 'customer', 'churned', 'lost_lead', 'lost_opportunity'] },
  { key: 'industry', label: 'Industry', type: 'text', operators: ['='] },
  { key: 'market', label: 'Market', type: 'text', operators: ['='] },
  { key: 'owner', label: 'Account owner', type: 'text', operators: ['='] },
  { key: 'sales_rep', label: 'Sales rep', type: 'text', operators: ['='] },
  { key: 'lead_source', label: 'Lead source', type: 'text', operators: ['='] },
  { key: 'activity_level', label: 'Activity level', type: 'text', operators: NUM },
  { key: 'mrr_change_30d_pct', label: 'MRR change, 30 days (%)', type: 'number', operators: NUM, computed: true },
  { key: 'mrr_change_90d_pct', label: 'MRR change, 90 days (%)', type: 'number', operators: NUM, computed: true },
  { key: 'days_since_change', label: 'Days since last change', type: 'number', operators: NUM, computed: true },
  { key: 'days_in_status', label: 'Days in current status', type: 'number', operators: NUM, computed: true },
  { key: 'changes_30d', label: 'Changes in the last 30 days', type: 'number', operators: NUM, computed: true },
  { key: 'mrr_percentile', label: 'MRR percentile (0-100)', type: 'number', operators: NUM, computed: true },
  { key: 'days_since_first_seen', label: 'Days since first seen', type: 'number', operators: NUM, computed: true },
  ...customFields.map((f) => ({ key: `custom:${f.id}`, label: f.name, type: 'text', operators: NUM, custom: true })),
]
// the team's AI settings (modules/ai_settings): a key "works" when it starts with AIza
const aiSettings = {
  mode: 'builtin' as 'builtin' | 'own',
  provider: 'gemini',
  model: '',
  hasKey: false,
  keyHint: undefined as string | undefined,
  verifiedAt: undefined as string | undefined,
  verifiedModel: undefined as string | undefined,
  available: true,
  builtinConfigured: true,
  ownKeysConfigured: true,
}
let aiKey = ''

function saveAiSettings(config: InternalAxiosRequestConfig, body: { mode?: string; apiKey?: string; model?: string }) {
  const key = (body.apiKey ?? '').trim()
  const model = body.model === undefined ? aiSettings.model : body.model.trim().replace(/^models\//, '')
  if (body.mode === 'own' || key) {
    const check = key || aiKey
    if (!check) return respond(config, { message: 'Enter your Gemini API key.' }, 400)
    if (!check.startsWith('AIza')) return respond(config, { message: 'Gemini rejected the key.' }, 422)
    if (model && !model.startsWith('gemini') && !model.startsWith('gemma'))
      return respond(config, { message: `The model "${model}" does not exist, or this key cannot use it.` }, 422)
    aiSettings.verifiedAt = new Date().toISOString()
    aiSettings.verifiedModel = model || 'gemma-4-26b-a4b-it'
    if (key) {
      aiKey = key
      aiSettings.hasKey = true
      aiSettings.keyHint = key.slice(-4)
    }
  }
  aiSettings.mode = body.mode === 'own' ? 'own' : 'builtin'
  aiSettings.model = model
  return respond(config, aiSettings)
}

const mcpKeys = [{ id: 1, teamId: 1, userId: 'demo', name: 'Claude Desktop', createdAt: new Date(Date.now() - 9 * DAY).toISOString() }]
const notifications = [
  { id: 1, message: 'HubSpot sync finished: 12 companies updated', type: 'success', read: false, created_at: new Date(Date.now() - 3_600_000).toISOString() },
  { id: 2, message: 'Import of leads-q3.xlsx failed on row 18', type: 'error', read: false, created_at: new Date(Date.now() - 2 * DAY).toISOString() },
  { id: 3, message: 'Erik joined the team', type: 'info', read: true, created_at: new Date(Date.now() - 4 * DAY).toISOString() },
]

// Slack: not connected until "Add to Slack", then in no channel yet, the way a
// real install starts. GET /integrations/slack/invite stands in for someone
// typing /invite @Metafor in Slack: #sales first, then a private #leadership.
type DemoSlackChannel = { channel_id: string; name: string; private: boolean; periods: string[]; next_wrap?: { date: string; period: string; name: string }; last_post_at?: string; last_error?: string }
type DemoSlack = { configured: boolean; connected: boolean; workspace?: string; timezone?: string; channels: DemoSlackChannel[] }
// kept for the tab: "Add to Slack" leaves the page and comes back, like the real one
const SLACK_KEY = 'metafor-demo-slack'
const slack: DemoSlack = (() => {
  try {
    return JSON.parse(sessionStorage.getItem(SLACK_KEY) ?? '') as DemoSlack
  } catch {
    return { configured: true, connected: false, channels: [] }
  }
})()
function saveSlack() {
  try {
    sessionStorage.setItem(SLACK_KEY, JSON.stringify(slack))
  } catch {
    /* ignore */
  }
}
function slackNext(periods: string[]) {
  if (!periods.length) return undefined
  const d = new Date()
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7))
  const first = d.getDate() <= 7
  const period = first && periods.includes('month') ? 'month' : periods.includes('week') ? 'week' : periods[0]
  return { date: d.toISOString().slice(0, 10), period, name: period === 'week' ? 'Last week' : 'Last month' }
}

function respond<T>(config: InternalAxiosRequestConfig, data: T, status = 200, headers: Record<string, string> = {}): Promise<AxiosResponse<T>> {
  // copies, like a real network response — the store mutates in place
  const copy = data instanceof Blob ? data : (structuredClone(data) as T)
  const res = { data: copy, status, statusText: String(status), headers, config, request: {} } as AxiosResponse<T>
  // a little latency so loading states are visible
  return new Promise((ok, fail) =>
    setTimeout(() => (status < 400 ? ok(res) : fail(Object.assign(new Error(`Request failed with status ${status}`), { response: res, config }))), 250),
  )
}

export const demoAdapter: AxiosAdapter = (config) => {
  const method = (config.method ?? 'get').toLowerCase()
  const path = (config.url ?? '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/api\/v1/, '')
  const p = (config.params ?? {}) as Record<string, string>
  const body = typeof config.data === 'string' ? JSON.parse(config.data || '{}') : config.data
  let m: RegExpMatchArray | null

  if (method === 'get' && path === '/ai-assistant/chat/history') return chatRows.length ? respond(config, { history: chatRows }) : respond(config, { error: 'Chat not found' }, 404)
  if (method === 'post' && path === '/ai-assistant/tool/execute') {
    const r = executeDemoTool(body.call.id, body.call.approval_status)
    return r ? respond(config, r) : respond(config, { error: 'function call not found' }, 404)
  }
  if (method === 'delete' && path === '/ai-assistant/chat') {
    chatRows.splice(0, chatRows.length)
    return respond(config, { message: 'All chats deleted successfully' })
  }

  if (path === '/team/ai-settings') return method === 'put' ? saveAiSettings(config, body) : respond(config, aiSettings)
  if (method === 'post' && path === '/team/ai-settings/test') {
    if (!aiKey) return respond(config, { message: 'No key is saved.' }, 400)
    aiSettings.verifiedAt = new Date().toISOString()
    return respond(config, aiSettings)
  }
  if (method === 'delete' && path === '/team/ai-settings/key') {
    aiKey = ''
    Object.assign(aiSettings, { mode: 'builtin', hasKey: false, keyHint: undefined, verifiedAt: undefined, verifiedModel: undefined })
    return respond(config, aiSettings)
  }

  if (path === '/integrations/slack') {
    if (method === 'delete') {
      Object.assign(slack, { connected: false, workspace: undefined, channels: [] })
      saveSlack()
      return respond(config, {}, 204)
    }
    if (method === 'put' && body?.timezone) slack.timezone = body.timezone
    saveSlack()
    return respond(config, slack)
  }
  if (method === 'get' && path === '/integrations/slack/install') {
    Object.assign(slack, { connected: true, workspace: 'Metafor Demo HQ', timezone: p.tz || 'Europe/Stockholm' })
    slack.channels = []
    saveSlack()
    return respond(config, { url: '/settings/slack?slack=connected' })
  }
  if (method === 'get' && path === '/integrations/slack/invite') {
    // not an api route: lets the demo stand in for "/invite @Metafor" in Slack
    const n = slack.channels.length
    slack.channels.push(n ? { channel_id: 'G02LEAD', name: 'leadership', private: true, periods: ['week', 'month', 'quarter', 'year'], next_wrap: slackNext(['week', 'month']) } : { channel_id: 'C01SALES', name: 'sales', private: false, periods: ['week', 'month', 'quarter', 'year'], next_wrap: slackNext(['week', 'month']) })
    saveSlack()
    return respond(config, slack)
  }
  if ((m = path.match(/^\/integrations\/slack\/channels\/([^/]+)(\/test)?$/))) {
    const ch = slack.channels.find((c) => c.channel_id === decodeURIComponent(m![1]))
    if (!ch) return respond(config, { message: 'The bot is not in that channel' }, 404)
    if (m[2]) {
      ch.last_post_at = new Date().toISOString()
      ch.last_error = undefined
      saveSlack()
      return respond(config, { period: ch.periods.includes('month') ? 'month' : ch.periods[0], name: 'Oct 2026' })
    }
    ch.periods = ['week', 'month', 'quarter', 'year'].filter((x) => (body.periods ?? []).includes(x))
    ch.next_wrap = slackNext(ch.periods)
    saveSlack()
    return respond(config, ch)
  }

  if (method === 'get') {
    if (path === '/team/current') return respond(config, team)
    if (path === '/user/teams') return respond(config, [{ id: 1, name: 'Metafor Demo', selected: true }, { id: 2, name: 'Nordic Sales' }])
    if (path === '/team/stats') return respond(config, { userCount: members.length, activeIntegrations: { hubspot: true } })
    if (path === '/team') return respond(config, team.team)
    if (path === '/team/users') return respond(config, members)
    if (path === '/team/subscription') return respond(config, { subscription })
    if (path === '/integrations/hubspot/status') return hubspot ? respond(config, hubspot) : respond(config, { error: 'Error retrieving record' }, 403)
    if (path === '/integrations/hubspot/fields') return respond(config, hubspotFields)
    if (path === '/integrations/hubspot/sync') {
      if (hubspot?.sync_status === 'in_progress') return respond(config, { error: 'Sync in progress' }, 409)
      if (hubspot) {
        const h = hubspot
        h.sync_status = 'in_progress'
        setTimeout(() => Object.assign(h, { sync_status: 'success', last_sync_success_date: new Date().toISOString() }), 8000)
      }
      return respond(config, { message: 'Sync started' })
    }
    if (path === '/customer/hubspot/overview') {
      const hs = latestAll().filter((c) => c.Source === 'hubspot')
      const statuses: Record<string, number> = {}
      hs.forEach((c) => (statuses[c.status] = (statuses[c.status] ?? 0) + 1))
      return respond(config, { total: hs.length, statuses })
    }
    if (path === '/integrations/zapier') return zapierKey ? respond(config, { ApiKey: zapierKey }) : respond(config, { error: 'Record not found' }, 404)
    if (path === '/notifications') return respond(config, notifications)
    if (path === '/customer/companies/stats') return respond(config, { count: latestAll().length })
    if (path === '/dashboard/stats') {
      const statusCounts: Record<string, number> = {}
      const sourceCounts: Record<string, number> = {}
      for (const c of latestAll()) {
        statusCounts[c.status] = (statusCounts[c.status] ?? 0) + 1
        sourceCounts[c.Source] = (sourceCounts[c.Source] ?? 0) + 1
      }
      return respond(config, { statusCounts, sourceCounts })
    }
    if (path === '/customer/list') {
      let list = latestAll()
      if (!p.offset && !p.search && !p.statuses && Number(p.pageSize) === 5) {
        list = [...list].sort((a, b) => +new Date(b.CreatedAt) - +new Date(a.CreatedAt))
        return respond(config, list.slice(0, 5))
      }
      if (p.search) list = list.filter((c) => `${c.name} ${c.crmId}`.toLowerCase().includes(p.search.toLowerCase()))
      if (p.statuses) list = list.filter((c) => p.statuses.split(',').includes(c.status))
      list = [...list].sort((a, b) => (p.sortOrder === 'desc' ? -1 : 1) * a.name.localeCompare(b.name))
      const off = Number(p.offset ?? 0)
      return respond(config, list.slice(off, off + Number(p.pageSize ?? 10)), 200, { 'x-total-count': String(list.length) })
    }
    if (path === '/customer/feed') {
      // like the API: newest date_created first, strictly older than the cursor
      const all = [...records].sort((a, b) => +new Date(b.DateCreated) - +new Date(a.DateCreated))
      const older = p.lastItem ? all.filter((r) => +new Date(r.DateCreated) < +new Date(String(p.lastItem))) : all
      return respond(config, older.slice(0, 20))
    }
    if (path === '/customer/custom-fields') return respond(config, customFields)
    if (path === '/alerts') return respond(config, { alerts })
    if (path === '/alerts/fields') return respond(config, { fields: alertFields() })
    if (path === '/integrations/mcp') return respond(config, mcpKeys)
    if ((m = path.match(/^\/customer\/company\/(.+)$/))) {
      const c = latestOf(decodeURIComponent(m[1]))
      return c ? respond(config, c) : respond(config, { message: 'Customer not found' }, 404)
    }
    if ((m = path.match(/^\/customer\/list\/([^/]+)(\/full)?$/))) return respond(config, history(decodeURIComponent(m[1]), !!m[2]))
    if ((m = path.match(/^\/customer\/(\d+)\/custom-fields\/values$/))) {
      const r = records.find((x) => x.Id === Number(m![1]))
      return respond(config, (r?.customFields ?? []).map((f) => ({ customFieldId: f.fieldId, value: f.fieldValue })))
    }
    if ((m = path.match(/^\/customer\/(\d+)$/))) {
      const r = records.find((x) => x.Id === Number(m![1]))
      return r ? respond(config, r) : respond(config, { message: 'Customer not found' }, 404)
    }
  }

  if (method === 'post') {
    if (path === '/notifications/mark-all-read') {
      notifications.forEach((n) => (n.read = true))
      return respond(config, {})
    }
    if (path === '/team/switch') return respond(config, {})
    if (path === '/team/users') {
      members.push({ id: Math.max(...members.map((m) => m.id)) + 1, user_id: `u-${Date.now()}`, team_id: 1, role: body.role, email: body.email, name: '' })
      return respond(config, {})
    }
    if (path === '/team/subscription/create-checkout-session') {
      subscription = { ...(subscription ?? {}), internal_plan_id: `premium_${body.billing_cycle}`, cancel_at_period_end: false, expired_at: new Date(Date.now() + 30 * DAY).toISOString() }
      return respond(config, { url: '/settings/subscription/success' })
    }
    if (path === '/customer/custom-fields') {
      if (customFields.some((f) => f.name.toLowerCase() === String(body.name).toLowerCase())) return respond(config, { message: 'A field with this name already exists' }, 409)
      const field = { id: Math.max(0, ...customFields.map((f) => f.id)) + 1, name: body.name, createdAt: new Date().toISOString() }
      customFields.push(field)
      return respond(config, field)
    }
    if (path === '/integrations/mcp/generate') {
      const key = { id: Math.max(0, ...mcpKeys.map((k) => k.id)) + 1, teamId: 1, userId: 'demo', name: body.name ?? '', createdAt: new Date().toISOString() }
      mcpKeys.push(key)
      return respond(config, { id: key.id, name: key.name, api_key: `mcp_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}` })
    }
    if (path === '/integrations/zapier/generate') {
      zapierKey = zapierKey ?? `zap_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
      return respond(config, { api_key: zapierKey })
    }
    if (path === '/team/subscription/create-portal-session') return respond(config, { url: '/settings/subscription' })
    if (path === '/team/subscription/resume') {
      if (subscription) subscription.cancel_at_period_end = false
      return respond(config, {})
    }
    if (path === '/customer') {
      const rec = { ...body, Id: nextId++, Source: 'manual', CreatedBy: 'demo', CreatedAt: new Date().toISOString(), EventType: 'Data Change' } as Company
      rec.customFields = (body.customFields ?? []).map((f: { fieldId: number; value: string }) => ({ fieldId: f.fieldId, fieldName: fieldName(f.fieldId), fieldValue: f.value }))
      records.push(rec)
      return respond(config, rec)
    }
    if (path.startsWith('/customer/export')) return respond(config, new Blob(['demo export'], { type: 'text/plain' }), 200, { 'content-disposition': 'attachment; filename="demo-export.txt"', 'content-type': 'text/plain' })
    if (path === '/customer/upload/xls')
      return respond(config, [
        { id: 2, name: 'Nordvik Logistik AB', crmId: '10200', action: 'updated', error: null },
        { id: 3, name: 'Vinga Marin', crmId: '10277', action: 'created', error: null },
        { id: 4, name: '', crmId: '', action: 'skipped', error: { name: 'Name is required', crmId: 'CRM ID is required' } },
      ])
  }

  if (method === 'put' && (m = path.match(/^\/team\/users\/(\d+)$/))) {
    const member = members.find((x) => x.id === Number(m![1]))
    if (member) member.role = body.role
    return respond(config, member ?? {})
  }

  if (method === 'put' && path === '/integrations/hubspot/update') {
    if (hubspot) {
      for (const [k, v] of Object.entries(body)) hubspot[k] = Array.isArray(v) && k !== 'custom_field_mappings' ? JSON.stringify(v) : v
    }
    return respond(config, hubspot ?? {})
  }

  if (method === 'put' && (m = path.match(/^\/customer\/custom-fields\/(\d+)$/))) {
    const field = customFields.find((f) => f.id === Number(m![1]))
    if (!field) return respond(config, { message: 'Field not found' }, 404)
    field.name = body.name
    records.forEach((r) => r.customFields?.forEach((f) => f.fieldId === field.id && (f.fieldName = field.name)))
    return respond(config, field)
  }

  if ((method === 'put' || method === 'delete') && (m = path.match(/^\/alerts\/([a-z-]+)$/))) {
    const i = alerts.findIndex((a) => a.id === m![1])
    if (i < 0) return respond(config, { message: 'Unknown alert' }, 404)
    if (method === 'delete') alerts[i] = structuredClone(alertDefaults[i])
    else {
      const trigger = body.trigger ?? alertDefaults[i].trigger
      if (body.enabled && !trigger) return respond(config, { message: 'choose a field before switching this alert on', field: 'trigger' }, 400)
      alerts[i] = { id: alerts[i].id, enabled: !!body.enabled, trigger }
    }
    return respond(config, alerts[i])
  }

  if (method === 'put' && path === '/team') {
    Object.assign(team.team, body)
    return respond(config, team.team)
  }

  if (method === 'delete') {
    if ((m = path.match(/^\/customer\/custom-fields\/(\d+)$/))) {
      const id = Number(m[1])
      const i = customFields.findIndex((f) => f.id === id)
      if (i >= 0) customFields.splice(i, 1)
      records.forEach((r) => r.customFields && (r.customFields = r.customFields.filter((f) => f.fieldId !== id)))
      return respond(config, {})
    }
    if ((m = path.match(/^\/integrations\/mcp\/(\d+)$/))) {
      const i = mcpKeys.findIndex((k) => k.id === Number(m![1]))
      if (i >= 0) mcpKeys.splice(i, 1)
      return respond(config, { message: 'API key removed' })
    }
    if ((m = path.match(/^\/team\/users\/(\d+)$/))) {
      const i = members.findIndex((x) => x.id === Number(m![1]))
      if (i >= 0) members.splice(i, 1)
      return respond(config, {})
    }
    if (path === '/team/wipe') {
      records.splice(0, records.length)
      return respond(config, {})
    }
    if (path === '/integrations/hubspot' || path === '/integrations/hubspot/remove') {
      if (path.endsWith('/remove')) for (let i = records.length - 1; i >= 0; i--) if (records[i].Source === 'hubspot') records.splice(i, 1)
      hubspot = null
      return respond(config, {})
    }
    if (path === '/customer/hubspot/actions/wipe') {
      for (let i = records.length - 1; i >= 0; i--) if (records[i].Source === 'hubspot') records.splice(i, 1)
      return respond(config, {})
    }
    if (path === '/integrations/zapier/remove') {
      zapierKey = null
      return respond(config, {})
    }
    if (path === '/team/subscription/cancel') {
      if (subscription) subscription.cancel_at_period_end = true
      return respond(config, {})
    }
  }

  if (method === 'put') {
    if ((m = path.match(/^\/customer\/(\d+)\/([a-zA-Z]+)$/))) {
      const r = records.find((x) => x.Id === Number(m![1]))
      const key = FIELDS.find(([, db]) => db === m![2])?.[2]
      if (r && key) {
        const targets = body.update_all_newer ? records.filter((x) => x.crmId === r.crmId && +new Date(x.DateCreated) >= +new Date(r.DateCreated)) : [r]
        targets.forEach((x) => ((x as unknown as Record<string, unknown>)[key] = key === 'mrr' ? Number(body.value) : body.value))
      }
      return respond(config, r ?? {})
    }
    if ((m = path.match(/^\/customer\/(\d+)$/))) {
      const i = records.findIndex((x) => x.Id === Number(m![1]))
      if (i >= 0) {
        const customFields = (body.customFields ?? []).map((f: { fieldId: number; value: string }) => ({ fieldId: f.fieldId, fieldName: fieldName(f.fieldId), fieldValue: f.value }))
        records[i] = { ...records[i], ...body, customFields, Id: records[i].Id }
      }
      return respond(config, records[i])
    }
  }

  if (method === 'delete' && (m = path.match(/^\/customer\/(\d+)$/))) {
    const i = records.findIndex((x) => x.Id === Number(m![1]))
    if (i >= 0) records.splice(i, 1)
    return respond(config, {})
  }

  return respond(config, { message: `demo: no fixture for ${method.toUpperCase()} ${path}` }, 404)
}
